/**
 * The security PIN's rules, mirrored from coloca-nest (`src/auth/step-up/security-pin.policy.ts`) so the form
 * can say what is wrong while the PIN is typed. The server checks again and is the authority: a PIN this lets
 * through can still answer 400 `SECURITY_PIN_WEAK` (with its own sentence) or `SECURITY_PIN_REUSED`.
 */

export const SECURITY_PIN_LENGTH = 6;

/** Consecutive wrong PINs before the 15-minute lock, and before a new PIN is required. */
export const SECURITY_PIN_SOFT_LOCK_ATTEMPTS = 5;
export const SECURITY_PIN_HARD_LOCK_ATTEMPTS = 10;

/**
 * Why a PIN is refused. `pattern` gathers the shapes people pick on purpose — two simple halves (111222,
 * 456123), keypad lines (147369), doubled digits (112233) and mirrors (123321).
 */
export type SecurityPinIssue =
  | "incomplete"
  | "repeated"
  | "sequence"
  | "pattern"
  | "date"
  | "common"
  | "phone";

export interface SecurityPinContext {
  /** The signed-in user's phone, any format: a PIN found among its digits is refused. */
  phone?: string | null;
}

const COMMON_PINS = new Set([
  "246810",
  "135790",
  "201314",
  "112358",
  "102030",
  "010203",
  "147258",
  "258369",
  "159753",
  "357159",
  "147852",
  "520131",
  "131420",
]);

/** Lines on a phone keypad, read either way: columns and diagonals (rows are runs, caught below). */
const KEYPAD_LINES = new Set(["147", "258", "369", "741", "852", "963", "159", "357", "951", "753"]);

const isRepeatOf = (pin: string, period: number): boolean => {
  for (let i = period; i < pin.length; i++) {
    if (pin[i] !== pin[i - period]) return false;
  }
  return true;
};

/** Every digit one step from the previous (mod 10), in one direction: 123456, 654321, 890123. */
const isRun = (digits: string, step: 1 | -1): boolean => {
  for (let i = 1; i < digits.length; i++) {
    if (Number(digits[i]) !== (Number(digits[i - 1]) + step + 10) % 10) return false;
  }
  return true;
};

const isDoubledDigits = (pin: string): boolean => {
  for (let i = 0; i < pin.length; i += 2) {
    if (pin[i] !== pin[i + 1]) return false;
  }
  return true;
};

const isPalindrome = (pin: string): boolean => pin === [...pin].reverse().join("");

/** 000…999, a three-digit run either way (wrapping: 901, 210) or a keypad line. */
const isSimpleTriple = (triple: string): boolean =>
  isRepeatOf(triple, 1) || isRun(triple, 1) || isRun(triple, -1) || KEYPAD_LINES.has(triple);

const isSimpleHalves = (pin: string): boolean =>
  isSimpleTriple(pin.slice(0, 3)) && isSimpleTriple(pin.slice(3));

const isDayMonth = (day: string, month: string): boolean => {
  const d = Number(day);
  const m = Number(month);
  return d >= 1 && d <= 31 && m >= 1 && m <= 12;
};

/** DDMMYY, MMDDYY or YYMMDD: a birthday is the first PIN anyone guesses. */
const isDateLike = (pin: string): boolean => {
  const [a, b, c] = [pin.slice(0, 2), pin.slice(2, 4), pin.slice(4, 6)];
  return isDayMonth(a, b) || isDayMonth(b, a) || isDayMonth(c, b);
};

const isInPhone = (pin: string, phone: string | null | undefined): boolean => {
  const digits = (phone ?? "").replace(/\D/g, "");
  return digits.length >= SECURITY_PIN_LENGTH && digits.includes(pin);
};

/** Why a PIN would be refused, or null when it is good to go. */
export function securityPinIssue(pin: string, context: SecurityPinContext = {}): SecurityPinIssue | null {
  if (!new RegExp(`^\\d{${SECURITY_PIN_LENGTH}}$`).test(pin)) return "incomplete";
  if (isRepeatOf(pin, 1)) return "repeated";
  if (isRun(pin, 1) || isRun(pin, -1)) return "sequence";
  if (
    isRepeatOf(pin, 2) ||
    isRepeatOf(pin, 3) ||
    isDoubledDigits(pin) ||
    isPalindrome(pin) ||
    isSimpleHalves(pin)
  ) {
    return "pattern";
  }
  if (COMMON_PINS.has(pin)) return "common";
  if (isDateLike(pin)) return "date";
  if (isInPhone(pin, context.phone)) return "phone";
  return null;
}
